import React, { useEffect, useRef, useState } from 'react';

type AnimationSnapshot = {
  filter?: string;
  opacity?: number;
  y?: number;
};

interface BlurTextProps {
  text?: string;
  delay?: number; // ms between characters / words
  className?: string;
  animateBy?: 'words' | 'letters';
  direction?: 'top' | 'bottom';
  threshold?: number;
  rootMargin?: string;
  animationFrom?: AnimationSnapshot;
  animationTo?: AnimationSnapshot[];
  onAnimationComplete?: () => void;
  stepDuration?: number; // seconds for each keyframe step
}

export default function BlurText({
  text = '',
  delay = 200,
  className = '',
  animateBy = 'words',
  threshold = 0.1,
  rootMargin = '0px',
  animationFrom,
  animationTo,
  onAnimationComplete,
  stepDuration = 0.35,
}: BlurTextProps) {
  const parts = animateBy === 'words' ? text.split(' ') : text.split('');
  const ref = useRef<HTMLParagraphElement | null>(null);
  const spanRefs = useRef<Array<HTMLElement | null>>([]);
  const [inView, setInView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (!ref.current || hasAnimated) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        if (e?.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold, rootMargin, hasAnimated]);

  useEffect(() => {
    if (!inView || hasAnimated) return;
    let tl: any = null;
    let mounted = true;

    const run = async () => {
      try {
        const gsapMod = await import('gsap');
        const gsap = (gsapMod.gsap || gsapMod.default || gsapMod) as any;
        // create timeline
        tl = gsap.timeline();

        spanRefs.current.forEach((el, i) => {
          if (!el) return;
          gsap.set(el, {
            opacity: animationFrom?.opacity ?? 0,
            y: animationFrom?.y ?? 20,
            filter: animationFrom?.filter ?? 'none',
          });

          const duration = stepDuration;
          tl.to(
            el,
            {
              opacity: animationTo?.[animationTo.length - 1]?.opacity ?? 1,
              y: animationTo?.[animationTo.length - 1]?.y ?? 0,
              filter: animationTo?.[animationTo.length - 1]?.filter ?? 'none',
              duration,
              ease: 'power2.out',
            },
            (i * delay) / 1000
          );
        });

        tl.call(() => {
          if (!mounted) return;
          setHasAnimated(true);
          onAnimationComplete && onAnimationComplete();
        });
      } catch (e) {
        // fail silently on server or if gsap isn't available
      }
    };

    run();

    return () => {
      mounted = false;
      try {
        tl?.kill && tl.kill();
      } catch {}
    };
  }, [inView, text, delay, animationFrom, animationTo, stepDuration, onAnimationComplete, hasAnimated]);

  return (
    <p ref={ref} className={`blur-text ${className} flex flex-wrap`}>
      {parts.map((part, i) => (
        <span
          key={i}
          ref={(el) => (spanRefs.current[i] = el)}
          className="inline-block will-change-[transform,filter,opacity]"
          style={{ display: 'inline-block' }}
        >
          {part === ' ' ? '\u00A0' : part}
          {animateBy === 'words' && i < parts.length - 1 ? '\u00A0' : ''}
        </span>
      ))}
    </p>
  );
}
